/**
 * Redeem Group API hooks for managing redeem groups
 * Provides React Query hooks for CRUD operations with cache management
 */

import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { authenticatedApi } from '../lib/authenticated-api';
import toast from 'react-hot-toast';
import type {
  RedeemGroup,
  CreateRedeemGroupInput,
  UpdateRedeemGroupInput,
  RedeemGroupListResponse,
  CodesListResponse,
  BatchListResponse,
} from '../lib/types/redeemGroup';

// Query keys for cache management
const redeemGroupQueryKeys = {
  all: ['redeemGroups'] as const,
  adminList: (limit?: number) => ['redeemGroups', 'admin-list', limit] as const,
  adminDetail: (groupId: string) =>
    ['redeemGroups', 'admin-detail', groupId] as const,
  codes: (groupId: string, redeemed?: boolean) =>
    ['redeemGroups', 'codes', groupId, { redeemed }] as const,
  codesForGroup: (groupId: string) =>
    ['redeemGroups', 'codes', groupId] as const,
  codeStatus: (code: string) => ['redeemGroups', 'code-status', code] as const,
  batches: (groupId: string) => ['redeemGroups', 'batches', groupId] as const,
};

/**
 * Hook to fetch list of redeem groups (admin endpoint)
 * Uses infinite query for pagination with "Load More" functionality
 */
export function useRedeemGroupsList(limit = 50) {
  return useInfiniteQuery<RedeemGroupListResponse>({
    queryKey: redeemGroupQueryKeys.adminList(limit),
    queryFn: async ({ pageParam }) => {
      const response = await authenticatedApi.listRedeemGroups({
        limit,
        startAfter: pageParam as string | undefined,
      });
      if (!response.data) {
        throw new Error('No data returned from API');
      }
      return response.data;
    },
    initialPageParam: undefined,
    getNextPageParam: lastPage => {
      // Return the groupId of the last item to use as cursor for next page
      if (lastPage.hasMore && lastPage.groups.length > 0) {
        return lastPage.groups[lastPage.groups.length - 1].groupId;
      }
      return undefined;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
  });
}

/**
 * Hook to fetch a single redeem group (admin endpoint)
 */
export function useRedeemGroup(groupId: string) {
  return useQuery({
    queryKey: redeemGroupQueryKeys.adminDetail(groupId),
    queryFn: async () => {
      const response = await authenticatedApi.getRedeemGroup(groupId);
      return response.data?.group;
    },
    enabled: !!groupId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to create a new redeem group
 * Invalidates the list cache on success
 */
export function useCreateRedeemGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateRedeemGroupInput) => {
      const response = await authenticatedApi.createRedeemGroup(data);
      return response.data?.group;
    },
    onSuccess: () => {
      // Invalidate all redeem group queries to trigger refetch
      queryClient.invalidateQueries({ queryKey: redeemGroupQueryKeys.all });
      toast.success('Redeem group created successfully');
    },
    onError: (error: unknown) => {
      let message = 'Failed to create redeem group';
      if (error && typeof error === 'object') {
        if ('response' in error) {
          const response = (
            error as { response?: { data?: { message?: string } } }
          ).response;
          if (response?.data?.message) {
            message = response.data.message;
          }
        } else if (
          'message' in error &&
          typeof (error as { message: unknown }).message === 'string'
        ) {
          message = (error as { message: string }).message;
        }
      }
      toast.error(message);
    },
  });
}

/**
 * Hook to update a redeem group
 * Uses optimistic updates for immediate UI feedback
 */
export function useUpdateRedeemGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      groupId,
      updates,
    }: {
      groupId: string;
      updates: UpdateRedeemGroupInput;
    }) => {
      const response = await authenticatedApi.updateRedeemGroup(
        groupId,
        updates
      );
      return response.data?.group;
    },
    onMutate: async ({ groupId, updates }) => {
      // Cancel any outgoing refetches to avoid overwriting optimistic update
      await queryClient.cancelQueries({
        queryKey: redeemGroupQueryKeys.adminDetail(groupId),
      });

      // Snapshot the previous value
      const previousGroup = queryClient.getQueryData<RedeemGroup>(
        redeemGroupQueryKeys.adminDetail(groupId)
      );

      // Optimistically update the cache
      if (previousGroup) {
        queryClient.setQueryData<RedeemGroup>(
          redeemGroupQueryKeys.adminDetail(groupId),
          {
            ...previousGroup,
            ...updates,
          }
        );
      }

      // Return context with the previous value
      return { previousGroup, groupId };
    },
    onSuccess: (_, variables) => {
      // Invalidate queries to refetch with server data
      queryClient.invalidateQueries({ queryKey: redeemGroupQueryKeys.all });
      queryClient.invalidateQueries({
        queryKey: redeemGroupQueryKeys.adminDetail(variables.groupId),
      });
      toast.success('Redeem group updated successfully');
    },
    onError: (error: unknown, _, context) => {
      // Rollback optimistic update on error
      if (context?.previousGroup && context?.groupId) {
        queryClient.setQueryData(
          redeemGroupQueryKeys.adminDetail(context.groupId),
          context.previousGroup
        );
      }
      let message = 'Failed to update redeem group';
      if (error && typeof error === 'object') {
        if ('response' in error) {
          const response = (
            error as { response?: { data?: { message?: string } } }
          ).response;
          if (response?.data?.message) {
            message = response.data.message;
          }
        } else if (
          'message' in error &&
          typeof (error as { message: unknown }).message === 'string'
        ) {
          message = (error as { message: string }).message;
        }
      }
      toast.error(message);
    },
  });
}

/**
 * Hook to delete a redeem group
 * Invalidates cache on success
 */
export function useDeleteRedeemGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (groupId: string) => {
      await authenticatedApi.deleteRedeemGroup(groupId);
    },
    onSuccess: () => {
      // Invalidate all redeem group queries to trigger refetch
      queryClient.invalidateQueries({ queryKey: redeemGroupQueryKeys.all });
      toast.success('Redeem group deleted successfully');
    },
    onError: (error: unknown) => {
      let message = 'Failed to delete redeem group';
      if (error && typeof error === 'object') {
        if ('response' in error) {
          const response = (
            error as { response?: { data?: { message?: string } } }
          ).response;
          if (response?.data?.message) {
            message = response.data.message;
          }
        } else if (
          'message' in error &&
          typeof (error as { message: unknown }).message === 'string'
        ) {
          message = (error as { message: string }).message;
        }
      }
      toast.error(message);
    },
  });
}

/**
 * Hook to generate codes for a redeem group
 * Invalidates cache on success and displays generation metrics
 */
export function useGenerateCodes() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      groupId,
      count,
      note,
    }: {
      groupId: string;
      count: number;
      note?: string;
    }) => {
      const response = await authenticatedApi.generateCodes(
        groupId,
        count,
        note
      );
      return response.data;
    },
    onSuccess: (data, variables) => {
      // Invalidate redeem group queries to trigger refetch
      queryClient.invalidateQueries({ queryKey: redeemGroupQueryKeys.all });
      queryClient.invalidateQueries({
        queryKey: redeemGroupQueryKeys.adminDetail(variables.groupId),
      });

      // Invalidate batch queries to show new batch in history
      queryClient.invalidateQueries({
        queryKey: redeemGroupQueryKeys.batches(variables.groupId),
      });

      // Show success message with duration info
      if (data) {
        const durationSeconds = (data.durationMs / 1000).toFixed(2);
        toast.success(
          `Generated ${data.totalCodes} codes in ${durationSeconds}s`
        );
      } else {
        toast.success('Codes generated successfully');
      }
    },
    onError: (error: unknown) => {
      let message = 'Failed to generate codes';
      if (error && typeof error === 'object') {
        if ('response' in error) {
          const response = (
            error as { response?: { data?: { message?: string } } }
          ).response;
          if (response?.data?.message) {
            message = response.data.message;
          }
        } else if (
          'message' in error &&
          typeof (error as { message: unknown }).message === 'string'
        ) {
          message = (error as { message: string }).message;
        }
      }
      toast.error(message);
    },
  });
}

/**
 * Hook to fetch codes for a redeem group with pagination and filtering
 * Uses infinite query for pagination with "Load More" functionality
 */
export function useGroupCodes(groupId: string, redeemed?: boolean, limit = 50) {
  return useInfiniteQuery<CodesListResponse>({
    queryKey: redeemGroupQueryKeys.codes(groupId, redeemed),
    queryFn: async ({ pageParam }) => {
      const response = await authenticatedApi.getGroupCodes(groupId, {
        limit,
        startAfter: pageParam as string | undefined,
        redeemed,
      });
      if (!response.data) {
        throw new Error('No data returned from API');
      }
      return response.data;
    },
    initialPageParam: undefined,
    getNextPageParam: lastPage => {
      // Return the code of the last item to use as cursor for next page
      if (lastPage.hasMore && lastPage.codes.length > 0) {
        return lastPage.codes[lastPage.codes.length - 1].code;
      }
      return undefined;
    },
    enabled: !!groupId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
  });
}

/**
 * Hook to check the status of a specific redeem code
 * Returns code metadata including redemption status
 */
export function useCodeStatus(code: string) {
  return useQuery({
    queryKey: redeemGroupQueryKeys.codeStatus(code),
    queryFn: async () => {
      const response = await authenticatedApi.getCodeStatus(code);
      return response.data;
    },
    enabled: !!code && code.trim().length > 0,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: false, // Don't retry on 404 (invalid code)
  });
}

/**
 * Hook to import codes from CSV file with automatic chunking
 * Invalidates group codes and stats on success
 * Supports progress callbacks for large imports
 */
export function useImportCodes() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      groupId,
      file,
      note,
      onProgress,
    }: {
      groupId: string;
      file: File;
      note?: string;
      onProgress?: (current: number, total: number) => void;
    }) => {
      const response = await authenticatedApi.importCodes(
        groupId,
        file,
        note,
        onProgress
      );
      return response.data;
    },
    onSuccess: (data, variables) => {
      // Invalidate codes queries for this group to trigger refetch
      queryClient.invalidateQueries({
        queryKey: redeemGroupQueryKeys.codesForGroup(variables.groupId),
      });

      // Invalidate group detail to update stats
      queryClient.invalidateQueries({
        queryKey: redeemGroupQueryKeys.adminDetail(variables.groupId),
      });

      // Invalidate batch queries to show new batch in history
      queryClient.invalidateQueries({
        queryKey: redeemGroupQueryKeys.batches(variables.groupId),
      });

      // Show success message
      if (data) {
        const total = data.imported + data.voided;
        const hasErrors = data.errors.length > 0;
        if (hasErrors) {
          toast.success(
            `Processed ${total} codes (${data.errors.length} errors)`
          );
        } else {
          toast.success(`Successfully imported ${data.imported} codes`);
        }
      }
    },
    onError: (error: unknown) => {
      let message = 'Failed to import codes';
      if (error && typeof error === 'object') {
        if ('response' in error) {
          const response = (
            error as { response?: { data?: { message?: string } } }
          ).response;
          if (response?.data?.message) {
            message = response.data.message;
          }
        } else if (
          'message' in error &&
          typeof (error as { message: unknown }).message === 'string'
        ) {
          message = (error as { message: string }).message;
        }
      }
      toast.error(message);
    },
  });
}

/**
 * Hook to fetch batch history for a redeem group
 * Uses infinite query for pagination with "Load More" functionality
 */
export function useBatchHistory(groupId: string, limit = 20) {
  return useInfiniteQuery<BatchListResponse>({
    queryKey: redeemGroupQueryKeys.batches(groupId),
    queryFn: async ({ pageParam }) => {
      const response = await authenticatedApi.listBatches(groupId, {
        limit,
        startAfter: pageParam as string | undefined,
      });
      if (!response.data) {
        throw new Error('No data returned from API');
      }
      return response.data;
    },
    initialPageParam: undefined,
    getNextPageParam: lastPage => {
      // Return the batch ID of the last item to use as cursor for next page
      if (lastPage.hasMore && lastPage.batches.length > 0) {
        return lastPage.batches[lastPage.batches.length - 1].id;
      }
      return undefined;
    },
    enabled: !!groupId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
  });
}

// Add export for redeemGroupQueryKeys to enable testing cache invalidation
export { redeemGroupQueryKeys };
